// Tile Skin Registry
// Each skin exports a renderTile(cell, opts) function that returns a style object
// and optionally a renderContent(cell, opts) function for custom inner content.
//
// opts: { selected, timerActive, timer, CELL_SIZE, COLORS }
//
// ─── Adding a new IMAGE skin (png/jpg/svg) ───────────────────────────
//
//   1. Place your image in  ./assets/  (e.g. assets/my-tile.png)
//   2. Import it at the top of this file:
//        import myTile from './assets/my-tile.png';
//   3. Call registerImageSkin:
//        registerImageSkin({
//          id:   'my-tile',      // unique key stored in settings
//          name: 'My Tile',      // display name in the skin picker
//          image: myTile,        // the imported image
//        });
//
//   Optional overrides (with defaults):
//     bg            '#2a2a2e'   tile background when empty / behind image
//     borderColor   '#fff'      tile border colour
//     textColor     '#fff'      number colour
//     fontSize      28          number font size
//     showNumber    true        show the cell value on top of the image
//
// ─── Adding a fully custom skin ──────────────────────────────────────
//
//   Call registerSkin({ id, name, preview, renderTile, renderFlyingTile?, renderContent? })
//   See the Basic and Neon skins below for examples.
//

// ── Image imports (add yours here) ───────────────────────────────────
// import exampleTile from './assets/example-tile.svg';

const SKINS = new Map();

function registerSkin(skin) {
  SKINS.set(skin.id, skin);
}

export function getSkin(id) {
  return SKINS.get(id) || SKINS.get('basic');
}

export function getAllSkins() {
  return [...SKINS.values()];
}

// ── Image skin helper ────────────────────────────────────────────────
// Creates a complete skin from a single imported image file.
// The image is shown as the tile background; the cell number overlays it.
export function registerImageSkin({
  id,
  name,
  image,
  bg = '#2a2a2e',
  borderColor = '#fff',
  textColor = '#fff',
  fontSize = 28,
  showNumber = true,
}) {
  registerSkin({
    id,
    name,
    preview: { bg, border: `2px solid ${borderColor}`, color: textColor, borderRadius: 8 },
    renderTile(cell, { selected, timerActive, timer, CELL_SIZE, COLORS }) {
      return {
        position: 'absolute',
        width: CELL_SIZE,
        height: CELL_SIZE,
        backgroundColor: selected ? COLORS.selected : bg,
        backgroundImage: cell ? `url(${image})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        color: textColor,
        fontWeight: 700,
        borderRadius: 8,
        cursor: timerActive && timer > 0 ? 'pointer' : 'default',
        opacity: cell ? 1 : 0.3,
        border: `2px solid ${selected ? COLORS.selected : borderColor}`,
        textShadow: '0 1px 4px rgba(0,0,0,0.7)',
      };
    },
    renderContent: showNumber ? undefined : () => null,
    renderFlyingTile(value, { CELL_SIZE }) {
      return {
        width: CELL_SIZE,
        height: CELL_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize,
        color: textColor,
        fontWeight: 700,
        borderRadius: 8,
        border: `2px solid ${borderColor}`,
        backgroundColor: bg,
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        textShadow: '0 1px 4px rgba(0,0,0,0.7)',
      };
    },
  });
}

// ── Basic (default) ──────────────────────────────────────────────────
registerSkin({
  id: 'basic',
  name: 'Basic',
  preview: { bg: '#393c41', border: '2px solid #fff', color: '#fff', borderRadius: 8 },
  renderTile(cell, { selected, timerActive, timer, CELL_SIZE, COLORS }) {
    return {
      position: 'absolute',
      width: CELL_SIZE,
      height: CELL_SIZE,
      background: selected ? COLORS.selected : COLORS.grid,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 32,
      color: '#fff',
      borderRadius: 8,
      cursor: timerActive && timer > 0 ? 'pointer' : 'default',
      opacity: cell ? 1 : 0.3,
      border: '2px solid #fff',
    };
  },
  // renderContent not needed — default uses cell value
  renderFlyingTile(value, { CELL_SIZE, COLORS }) {
    return {
      width: CELL_SIZE,
      height: CELL_SIZE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 32,
      color: '#fff',
      borderRadius: 8,
      border: '2px solid #fff',
      background: COLORS.selected,
    };
  },
});

// ── Neon ─────────────────────────────────────────────────────────────
const NEON_PALETTE = [
  null,          // 0 — unused
  '#ff2d55',     // 1
  '#ff9500',     // 2
  '#ffcc00',     // 3
  '#4cd964',     // 4
  '#5ac8fa',     // 5
  '#007aff',     // 6
  '#5856d6',     // 7
  '#af52de',     // 8
  '#ff2d95',     // 9
];

registerSkin({
  id: 'neon',
  name: 'Neon',
  preview: { bg: '#1a1a2e', border: '2px solid #ff2d55', color: '#ff2d55', borderRadius: 6, boxShadow: '0 0 6px #ff2d55' },
  renderTile(cell, { selected, timerActive, timer, CELL_SIZE, COLORS }) {
    // eslint-disable-next-line security/detect-object-injection -- array lookup by numeric cell value (1-9), not user input
    const glow = cell ? NEON_PALETTE[cell] || '#fff' : '#333';
    return {
      position: 'absolute',
      width: CELL_SIZE,
      height: CELL_SIZE,
      background: selected ? COLORS.selected : '#1a1a2e',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 30,
      fontWeight: 700,
      color: selected ? '#fff' : glow,
      borderRadius: 6,
      cursor: timerActive && timer > 0 ? 'pointer' : 'default',
      opacity: cell ? 1 : 0.15,
      border: `2px solid ${selected ? '#fff' : glow}`,
      boxShadow: cell && !selected ? `0 0 8px ${glow}88, inset 0 0 6px ${glow}44` : 'none',
      textShadow: cell && !selected ? `0 0 8px ${glow}` : 'none',
      transition: 'box-shadow 0.15s ease',
    };
  },
  renderFlyingTile(value, { CELL_SIZE, COLORS }) {
    // eslint-disable-next-line security/detect-object-injection -- array lookup by numeric cell value (1-9), not user input
    const glow = value ? NEON_PALETTE[value] || '#fff' : '#333';
    return {
      width: CELL_SIZE,
      height: CELL_SIZE,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 30,
      fontWeight: 700,
      color: glow,
      borderRadius: 6,
      border: `2px solid ${glow}`,
      background: '#1a1a2e',
      boxShadow: `0 0 12px ${glow}aa`,
      textShadow: `0 0 10px ${glow}`,
    };
  },
});

// ── Image skins ──────────────────────────────────────────────────────
// To add a new image skin, copy the block below, changing id / name / image.

/*registerImageSkin({
  id: 'example',
  name: 'Example',
  image: exampleTile,
  // bg: '#2a2a2e',       // optional: background colour
  // borderColor: '#fff',  // optional: border colour
  // textColor: '#fff',    // optional: number colour
  // showNumber: true,     // optional: set false to hide the number
});
*/
export default SKINS;
